// App.js
import React, { useState, useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import MovieGrid from './components/MovieGrid';
import SearchBar from './components/SearchBar';
import SimilarityView from './components/SimilarityView';
import ExplainabilityPanel from './components/ExplainabilityPanel';
import AIFeatureToggles from './components/AIFeatureToggles';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const isSingleTokenQuery = (term) =>
  term.trim().split(/\s+/).filter(Boolean).length === 1;

// SVG Logo component
const MovieLogo = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.82 2H4.18C2.97602 2 2 2.97602 2 4.18V19.82C2 21.024 2.97602 22 4.18 22H19.82C21.024 22 22 21.024 22 19.82V4.18C22 2.97602 21.024 2 19.82 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7 2V22" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M17 2V22" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2 12H22" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2 7H7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2 17H7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M17 17H22" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M17 7H22" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Search mode is always 'semantic' — LangGraph AI parser + pgvector

function App() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [currentView, setCurrentView] = useState('search'); // 'search' or 'similarity'
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [explainability, setExplainability] = useState(null);
  // AI features default ON. Each toggle adds 1 LLM call (~500ms, ~$0.0001)
  // per search. Users can flip them off via the AI Features panel.
  const [aiFeatureFlags, setAiFeatureFlags] = useState({
    enable_hyde: true,
    enable_query_expansion: true,
    enable_rrf: true,
    enable_reranker: true,
  });
  const [aiFeaturesInfo, setAiFeaturesInfo] = useState(null);
  const prevQueryRef = useRef(''); // Ref to store the previous query
  const prevFlagsRef = useRef(aiFeatureFlags); // Ref for feature flags

  // Search function for a specific mode
  const searchWithMode = async (searchTerm, mode) => {
    // Build query params including AI feature flags
    const params = new URLSearchParams({
      q: searchTerm,
      mode: mode,
    });
    // Add enabled AI feature flags
    Object.entries(aiFeatureFlags).forEach(([key, enabled]) => {
      if (enabled) params.append(key, 'true');
    });
    const searchUrl = `${API_URL}/search?${params.toString()}`;
    const response = await fetch(searchUrl);

    if (response.ok) {
      return await response.json();
    } else {
      throw new Error(`Search API returned ${response.status}`);
    }
  };

  // Build WebSocket URL from the API base URL
  const getWsUrl = () => {
    try {
      const url = new URL(API_URL, window.location.origin);
      const wsScheme = url.protocol === 'https:' ? 'wss:' : 'ws:';
      // API_URL ends with /api or similar — strip it then append /api/ws/search
      const base = url.pathname.replace(/\/api\/?$/, '');
      return `${wsScheme}//${url.host}${base}/api/ws/search`;
    } catch (e) {
      // API_URL is a relative path
      const wsScheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${wsScheme}//${window.location.host}/api/ws/search`;
    }
  };

  // Streaming search via WebSocket. Lexical results show in ~150ms;
  // pipeline steps appear as each phase finishes; final reranked list lands
  // last. Falls back to HTTP /api/search if WebSocket fails.
  const searchViaWebSocket = (searchTerm) => {
    return new Promise((resolve, reject) => {
      let ws;
      try {
        ws = new WebSocket(getWsUrl());
      } catch (e) {
        return reject(e);
      }

      let gotAnything = false;
      let lastParsed = null;
      // Build the explainability object incrementally as `pipeline_step`
      // entries arrive. The `final_results` message replaces it with the
      // complete object. This keeps the bar visible the whole time and
      // shows the user real-time pipeline progress.
      const progressiveExplainability = {
        query: searchTerm,
        processed_query: searchTerm,
        search_type: 'lexical',
        search_type_label: 'Searching…',
        result_count: 0,
        total_latency_ms: 0,
        steps: [],
        features_detected: [],
        relevance_explanation: 'Streaming search in progress — pipeline steps will appear as they complete.',
        ai_features: {
          hyde: { enabled: aiFeatureFlags.enable_hyde, applied: false, latency_ms: 0 },
          query_expansion: { enabled: aiFeatureFlags.enable_query_expansion, applied: false, latency_ms: 0 },
          rrf: { enabled: aiFeatureFlags.enable_rrf, applied: false, latency_ms: 0 },
          reranker: { enabled: aiFeatureFlags.enable_reranker, applied: false, latency_ms: 0 },
        },
      };
      const timer = setTimeout(() => {
        if (!gotAnything) {
          try { ws.close(); } catch (_) {}
          reject(new Error('WS timeout'));
        }
      }, 5000);

      ws.onopen = () => {
        ws.send(JSON.stringify({
          query: searchTerm,
          mode: 'enhanced',
          top_k: 100,
          lexical_only: isSingleTokenQuery(searchTerm),
          ...aiFeatureFlags,
        }));
      };

      ws.onmessage = (event) => {
        gotAnything = true;
        clearTimeout(timer);
        let msg;
        try { msg = JSON.parse(event.data); } catch (_) { return; }

        // Append the just-completed pipeline step to the explainability
        // and re-render the bar so the user can see the progress live.
        if (msg.pipeline_step) {
          progressiveExplainability.steps = [
            ...progressiveExplainability.steps,
            msg.pipeline_step,
          ];
          progressiveExplainability.total_latency_ms =
            (progressiveExplainability.total_latency_ms || 0) +
            (msg.pipeline_step.details?.latency_ms || 0);
          // Shallow-clone to trigger a React re-render
          setExplainability({ ...progressiveExplainability });
        }

        if (msg.type === 'lexical_results') {
          // First chunk — show results immediately
          const processed = processResults(msg.results || []);
          setResults(processed);
          // Always set suggestions, even if empty, so the UI clears them
          // between searches.
          setSuggestions(msg.suggestions || []);
          progressiveExplainability.result_count = processed.length;
          if (processed.length > 0) {
            setStatus(
              isSingleTokenQuery(searchTerm)
                ? `⚡ ${processed.length} lexical results in ${msg.latency_ms}ms`
                : `⚡ ${processed.length} lexical results in ${msg.latency_ms}ms — enriching with semantic search…`
            );
          } else {
            // Lexical found nothing; semantic is the primary path. Don't
            // suggest "lexical results found" if there were none.
            setStatus(
              isSingleTokenQuery(searchTerm)
                ? `No lexical results found for "${searchTerm}"`
                : `No exact title/cast match — running semantic search across plot embeddings…`
            );
          }
        } else if (msg.type === 'parsed') {
          lastParsed = msg.parsed;
          if (msg.parsed?.filters?.length) {
            progressiveExplainability.features_detected = msg.parsed.filters.map((f) => ({
              [f.field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())]: f.value,
            }));
          }
          if (msg.metadata_only) {
            setStatus('🎯 Metadata filters detected — running pure attribute lookup…');
          }
          // Otherwise leave the lexical status visible so the user keeps
          // seeing "lexical results — enriching..." through the parse phase.
        } else if (msg.type === 'semantic_results') {
          const processed = processResults(msg.results || []);
          if (processed.length > 0) {
            setResults(processed);
            progressiveExplainability.result_count = processed.length;
            const sourceLabel = msg.source === 'metadata_filter'
              ? 'metadata-matched'
              : msg.source === 'pgvector'
                ? 'semantically-similar'
                : msg.source;
            setStatus(`🔮 ${processed.length} ${sourceLabel} results — finalising rank…`);
          }
        } else if (msg.type === 'final_results') {
          const processed = processResults(msg.results || []);
          if (processed.length > 0) {
            setResults(processed);
          }
          if (Array.isArray(msg.suggestions)) {
            setSuggestions(msg.suggestions);
          }
          if (msg.ai_features) setAiFeaturesInfo(msg.ai_features);
          // Replace progressive object with the server's complete one
          if (msg.explainability) setExplainability(msg.explainability);
          const parseNote = lastParsed && lastParsed.filters?.length > 0
            ? ` | Parsed: "${lastParsed.semantic_query}" + ${lastParsed.filters.length} filter(s)`
            : '';
          // Friendly final source label
          const finalLabel = msg.source === 'metadata_filter'
            ? 'metadata-filter'
            : msg.source === 'rrf_fusion'
              ? 'lexical+semantic fused'
              : msg.source === 'pgvector'
                ? 'semantic'
                : 'lexical';
          setStatus(
            `✓ Showing ${processed.length} ${finalLabel} results for "${searchTerm}" (${msg.latency_ms}ms)${parseNote}`
          );
          try { ws.close(); } catch (_) {}
          resolve();
        } else if (msg.type === 'error') {
          try { ws.close(); } catch (_) {}
          reject(new Error(msg.error || 'WS error'));
        }
      };

      ws.onerror = (e) => {
        clearTimeout(timer);
        if (!gotAnything) reject(e);
      };

      ws.onclose = () => {
        clearTimeout(timer);
        if (!gotAnything) reject(new Error('WS closed without data'));
      };
    });
  };

  // Search function (not debounced yet, will be wrapped in useEffect)
  const search = async (searchTerm) => {
    if (searchTerm.length < 3) return;

    setIsLoading(true);
    // Don't blank the explainability bar — keep the previous one visible
    // until the new one is ready, so the UI doesn't flicker between
    // searches. The WS handler will replace it as fresh pipeline_step
    // messages arrive.
    setStatus(`Searching for "${searchTerm}"...`);

    // Try WebSocket streaming first; fall back to HTTP if anything fails
    try {
      await searchViaWebSocket(searchTerm);
      setIsLoading(false);
      return;
    } catch (wsErr) {
      console.warn('[Search] WS failed, falling back to HTTP:', wsErr);
      // Fall through to HTTP
    }

    try {
      const data = await searchWithMode(searchTerm, 'semantic');

      if (data.explainability) setExplainability(data.explainability);
      if (data.ai_features) setAiFeaturesInfo(data.ai_features);

      let resultsData;
      if (data.results) resultsData = data.results;
      else if (Array.isArray(data)) resultsData = data;
      else if (typeof data === 'object') resultsData = data;

      if (resultsData) {
        const suggestionsArray = Array.isArray(data.suggestions) ? data.suggestions : [];
        setSuggestions(suggestionsArray);
        const processed = processResults(resultsData);
        setResults(processed);
        const latency = data.latency_ms || data.explainability?.total_latency_ms || 'N/A';
        const parsedInfo = data.parsed;
        const parseNote = parsedInfo && !parsedInfo.fallback_used && parsedInfo.filters?.length > 0
          ? ` | Parsed: "${parsedInfo.semantic_query}" + ${parsedInfo.filters.length} filter(s)`
          : '';
        setStatus(`Showing ${processed.length} results for "${searchTerm}" (${latency}ms)${parseNote}`);
      } else {
        setResults([]);
        setStatus(`No results found for "${searchTerm}"`);
      }
    } catch (error) {
      console.error('[Search] Error:', error);
      setResults([]);
      setExplainability(null);
      setStatus(`Error searching for "${searchTerm}"`);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced version of search, created once
  // 250ms debounce — short enough that lexical search still feels instant
  // (p50 ~150ms server time + 250ms debounce ≈ 400ms perceived), but long
  // enough to not fire a search on every keystroke during fast typing.
  const debouncedSearch = debounce(search, 250);

  // Process results
  const processResults = (results) => {
    const processedResults = [];
    const seenIds = new Set();

    const addMovie = (movie) => {
      if (!movie || typeof movie !== 'object') return;

      const movieId = movie.imdbID ||
        (movie.Title && movie.Year ? `${movie.Title}-${movie.Year}` : null);

      if (!movieId || seenIds.has(movieId)) return;

      seenIds.add(movieId);
      processedResults.push(movie);
    };

    if (Array.isArray(results)) {
      // Direct array of movies (semantic/feature search results)
      results.forEach(movie => addMovie(movie));
    } else if (results && results.results) {
      // Nested results object
      return processResults(results.results);
    } else if (typeof results === 'object' && results !== null) {
      // Dictionary structure (lexical search: {prefix: [movies]} or {prefix: {title: [movies]}})
      Object.keys(results).forEach(queryKey => {
        const queryResults = results[queryKey];

        if (Array.isArray(queryResults)) {
          // Lexical search format: {prefix: [movie1, movie2, ...]}
          queryResults.forEach(movie => addMovie(movie));
        } else if (typeof queryResults === 'object' && queryResults !== null) {
          // Nested format: {prefix: {movieTitle: [movies]}}
          Object.keys(queryResults).forEach(movieTitle => {
            const movies = queryResults[movieTitle];

            if (Array.isArray(movies)) {
              movies.forEach(movie => {
                if (movie && typeof movie === 'object') {
                  if (!movie.Title && movieTitle) {
                    movie.Title = movieTitle;
                  }
                  addMovie(movie);
                }
              });
            } else if (movies && typeof movies === 'object') {
              const movie = movies;
              if (!movie.Title && movieTitle) {
                movie.Title = movieTitle;
              }
              addMovie(movie);
            }
          });
        }
      });
    }

    return processedResults;
  };

  // Toggle handler — flips flag; useEffect below triggers re-search after state updates
  const handleToggleFeature = (featureKey) => {
    setAiFeatureFlags((prev) => ({ ...prev, [featureKey]: !prev[featureKey] }));
  };

  // Re-search when query changes (debounced)
  useEffect(() => {
    if (query !== prevQueryRef.current) {
      prevQueryRef.current = query;

      if (query.length >= 3) {
        debouncedSearch(query);
      } else {
        setResults([]);
        setSuggestions([]);
        setStatus('Type at least 3 characters to search');
      }
    }

    return () => {
      debouncedSearch.cancel();
    };
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-search immediately when AI feature flags change (uses fresh closure)
  useEffect(() => {
    const flagsChanged = JSON.stringify(aiFeatureFlags) !== JSON.stringify(prevFlagsRef.current);
    if (flagsChanged && query.length >= 3) {
      prevFlagsRef.current = aiFeatureFlags;
      search(query);
    } else {
      prevFlagsRef.current = aiFeatureFlags;
    }
  }, [aiFeatureFlags]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectSuggestion = (suggestion) => {
    setSuggestions([]);
    setQuery(suggestion);

    const contentContainer = document.querySelector('.content-container');
    if (contentContainer) {
      contentContainer.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleMovieClick = (movie) => {
    console.log('Movie clicked:', movie);
    console.log('Movie has imdbID:', movie.imdbID);
    setSelectedMovie(movie);
    setCurrentView('similarity');
  };

  const handleSimilarMovieClick = (movie) => {
    // When a movie is clicked from similarity view, update the selected movie
    setSelectedMovie(movie);
    // The SimilarityView component will automatically fetch new data
  };

  const handleBackToResults = () => {
    setCurrentView('search');
    setSelectedMovie(null);
  };

  return (
    <div className="app-container">
      {currentView === 'search' && (
        <>
          <header className="app-header">
            <div className="logo-container">
              <MovieLogo />
              <h1 className="app-title">CineSearch</h1>
            </div>
            <p className="app-subtitle">Discover your next favorite movie</p>
            <SearchBar
              value={query}
              onChange={setQuery}
              suggestions={suggestions}
              onSelectSuggestion={handleSelectSuggestion}
            />
            <AIFeatureToggles
              featureFlags={aiFeatureFlags}
              onToggle={handleToggleFeature}
              aiFeaturesInfo={aiFeaturesInfo}
            />
          </header>

          {status && <div className="status-message">{status}</div>}

          <div className="explainability-wrapper">
            <ExplainabilityPanel
              explainability={explainability}
              isLoading={isLoading && query.length >= 3}
            />
          </div>

          <main className="content-container">
            <MovieGrid
              movies={results}
              isLoading={isLoading}
              onMovieClick={handleMovieClick}
            />
          </main>
        </>
      )}

      {currentView === 'similarity' && (
        <main className="content-container">
          <SimilarityView
            selectedMovie={selectedMovie}
            onBackToResults={handleBackToResults}
            onMovieClick={handleSimilarMovieClick}
          />
        </main>
      )}
    </div>
  );
}

export default App;
